import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link as MuiLink,
  Collapse,
  TextField
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Mail as MailIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PictureAsPdf as PdfIcon,
  Code as CodeIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import XmlViewerDialog from '../components/XmlViewerDialog';
import { SabrinaService } from '../services/sabrinaService';
import {
  SabrinaNachricht,
  SabrinaNachrichtInfo,
  SabrinaNachrichtStatus,
  SabrinaPosteingangResult,
  SabrinaAbrufResult,
  SabrinaEmpfangeneNachricht,
  SabrinaPosteingang
} from '../types/sabrina';

// Cooldown-Zeit in Sekunden (KBA erlaubt max 1x pro Minute)
const POLLING_COOLDOWN_SECONDS = 60;

const SabrinaPage: React.FC = () => {
  const navigate = useNavigate();

  // State für Posteingang (KBA-Call)
  const [posteingangResult, setPosteingangResult] = useState<SabrinaPosteingangResult | null>(null);
  const [posteingangLoading, setPosteingangLoading] = useState(false);
  const [posteingangError, setPosteingangError] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [lastPosteingangCheck, setLastPosteingangCheck] = useState<Date | null>(null);

  // State für Posteingang-Liste (aus DB)
  const [posteingangListe, setPosteingangListe] = useState<SabrinaPosteingang[]>([]);
  const [posteingangListeLoading, setPosteingangListeLoading] = useState(false);

  // State für verfügbare Nachrichten zum Abruf
  const [selectedNachrichtenIds, setSelectedNachrichtenIds] = useState<string[]>([]);
  const [abrufLoading, setAbrufLoading] = useState(false);
  const [abrufResult, setAbrufResult] = useState<SabrinaAbrufResult | null>(null);

  // State für empfangene Nachrichten (Hauptbereich)
  const [empfangeneNachrichten, setEmpfangeneNachrichten] = useState<SabrinaEmpfangeneNachricht[]>([]);
  const [empfangeneLoading, setEmpfangeneLoading] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  // State für Kommunikationsnachrichten-Dialog
  const [kommDialogOpen, setKommDialogOpen] = useState(false);
  const [kommNachrichten, setKommNachrichten] = useState<SabrinaNachricht[]>([]);
  const [kommLoading, setKommLoading] = useState(false);

  // State für Detail-Dialog (Kommunikationsnachricht)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedNachricht, setSelectedNachricht] = useState<SabrinaNachricht | null>(null);

  // Snackbar für Feedback
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Pagination State für empfangene Nachrichten
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Pagination State für Kommunikationsnachrichten-Dialog
  const [kommPage, setKommPage] = useState(0);
  const [kommRowsPerPage, setKommRowsPerPage] = useState(10);

  // Filter für Kommunikationsnachrichten-Dialog
  const [kommVon, setKommVon] = useState<string>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [kommBis, setKommBis] = useState<string>(new Date().toISOString().split('T')[0]);

  // XML-Dialog State (Kommunikationsnachrichten)
  const [xmlDialog, setXmlDialog] = useState<{ open: boolean; title: string; content: string }>({ open: false, title: '', content: '' });
  const [xmlLoading, setXmlLoading] = useState(false);

  // Cooldown Timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  // Empfangene Nachrichten + Posteingang-Liste beim Laden der Seite abrufen
  useEffect(() => {
    loadEmpfangeneNachrichten();
    loadPosteingangListe();
  }, []);

  // Empfangene Nachrichten laden
  const loadEmpfangeneNachrichten = useCallback(async () => {
    setEmpfangeneLoading(true);
    try {
      const data = await SabrinaService.getEmpfangeneNachrichten();
      setEmpfangeneNachrichten(data);
    } catch (error: any) {
      console.error('Fehler beim Laden der empfangenen Nachrichten:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der empfangenen Nachrichten',
        severity: 'error'
      });
    } finally {
      setEmpfangeneLoading(false);
    }
  }, []);

  // Posteingang-Liste aus DB laden (ohne KBA-Call)
  const loadPosteingangListe = useCallback(async () => {
    setPosteingangListeLoading(true);
    try {
      const data = await SabrinaService.getPosteingangListe();
      setPosteingangListe(data);
    } catch (error: any) {
      console.error('Fehler beim Laden der Posteingang-Liste:', error);
    } finally {
      setPosteingangListeLoading(false);
    }
  }, []);

  // Kommunikationsnachrichten laden (für Dialog)
  const loadKommNachrichten = useCallback(async () => {
    setKommLoading(true);
    try {
      const data = await SabrinaService.getAlleNachrichtenListe();
      setKommNachrichten(data);
    } catch (error: any) {
      console.error('Fehler beim Laden der Kommunikationsnachrichten:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Kommunikationsnachrichten',
        severity: 'error'
      });
    } finally {
      setKommLoading(false);
    }
  }, []);

  // Posteingang prüfen (KBA-Call)
  const handleCheckPosteingang = async () => {
    if (cooldownSeconds > 0) return;

    setPosteingangLoading(true);
    setPosteingangError(null);
    setPosteingangResult(null);
    setSelectedNachrichtenIds([]);

    try {
      const result = await SabrinaService.checkPosteingang();
      setPosteingangResult(result);
      setLastPosteingangCheck(new Date());
      setCooldownSeconds(POLLING_COOLDOWN_SECONDS);

      // Posteingang-Liste aktualisieren
      await loadPosteingangListe();

      if (result.erfolg && result.anzahlNachrichten > 0) {
        setSnackbar({
          open: true,
          message: `${result.anzahlNachrichten} neue Nachricht(en) verfügbar`,
          severity: 'success'
        });
      } else if (result.hinweis) {
        setSnackbar({
          open: true,
          message: result.hinweis,
          severity: 'info'
        });
      }
    } catch (error: any) {
      console.error('Fehler beim Prüfen des Posteingangs:', error);
      if (error.response?.status === 429) {
        setPosteingangError('Polling-Limit erreicht. Bitte warten Sie mindestens 60 Sekunden zwischen den Anfragen.');
        setCooldownSeconds(POLLING_COOLDOWN_SECONDS);
      } else {
        setPosteingangError(error.response?.data?.message || 'Fehler beim Prüfen des Posteingangs');
      }
    } finally {
      setPosteingangLoading(false);
    }
  };

  // Nachrichten abrufen
  const handleAbrufNachrichten = async () => {
    if (selectedNachrichtenIds.length === 0) return;

    setAbrufLoading(true);
    setAbrufResult(null);

    try {
      const result = await SabrinaService.abrufNachrichten(selectedNachrichtenIds);
      setAbrufResult(result);

      if (result.erfolg) {
        setSnackbar({
          open: true,
          message: `${result.anzahlAbgerufen} Nachricht(en) erfolgreich abgerufen`,
          severity: 'success'
        });
        // Empfangene Nachrichten und Posteingang-Liste neu laden
        await Promise.all([
          loadEmpfangeneNachrichten(),
          loadPosteingangListe()
        ]);
        // Auswahl und Posteingang-Ergebnis zurücksetzen
        setSelectedNachrichtenIds([]);
        setPosteingangResult(null);
      } else if (result.fehler.length > 0) {
        setSnackbar({
          open: true,
          message: `${result.fehler.length} Fehler beim Abruf`,
          severity: 'warning'
        });
      }
    } catch (error: any) {
      console.error('Fehler beim Abrufen der Nachrichten:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Fehler beim Abrufen der Nachrichten',
        severity: 'error'
      });
    } finally {
      setAbrufLoading(false);
    }
  };

  // Checkbox-Handler für Posteingang
  const handleToggleNachricht = (nachrichtenId: string) => {
    setSelectedNachrichtenIds(prev =>
      prev.includes(nachrichtenId)
        ? prev.filter(id => id !== nachrichtenId)
        : [...prev, nachrichtenId]
    );
  };

  // Alle Posteingang-Nachrichten auswählen/abwählen
  const handleSelectAll = () => {
    if (posteingangListe.length === 0) return;
    if (selectedNachrichtenIds.length === posteingangListe.length) {
      setSelectedNachrichtenIds([]);
    } else {
      setSelectedNachrichtenIds(posteingangListe.map(n => n.sabrinaNachrichtenId));
    }
  };

  // Kommunikationsnachrichten-Dialog öffnen
  const handleOpenKommDialog = () => {
    setKommDialogOpen(true);
    loadKommNachrichten();
  };

  // Detail-Dialog öffnen (für Kommunikationsnachrichten)
  const handleOpenDetail = (nachricht: SabrinaNachricht) => {
    setSelectedNachricht(nachricht);
    setDetailDialogOpen(true);
  };

  // CSV-Export der Kommunikationsnachrichten
  const handleExportKommCsv = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    SabrinaService.exportKommunikationCsv(kommNachrichten, `sabrina_kommunikation_${timestamp}.csv`);
  };

  // XML anzeigen (lokal, falls bereits vorhanden)
  const handleShowXml = (title: string, content: string | undefined) => {
    setXmlDialog({ open: true, title, content: content || 'Kein XML-Inhalt vorhanden' });
  };

  // XML on-demand laden: Request-XML einer Kommunikationsnachricht
  const handleShowNachrichtRequestXml = async (id: number) => {
    setXmlDialog({ open: true, title: 'Request XML', content: '' });
    setXmlLoading(true);
    try {
      const xml = await SabrinaService.getNachrichtRequestXml(id);
      setXmlDialog(prev => prev.open ? { open: true, title: 'Request XML', content: xml || 'Kein XML-Inhalt vorhanden' } : prev);
    } catch {
      setXmlDialog(prev => prev.open ? { open: true, title: 'Request XML', content: 'Fehler beim Laden des XML-Inhalts' } : prev);
    } finally {
      setXmlLoading(false);
    }
  };

  // XML on-demand laden: Response-XML einer Kommunikationsnachricht
  const handleShowNachrichtResponseXml = async (id: number) => {
    setXmlDialog({ open: true, title: 'Response XML', content: '' });
    setXmlLoading(true);
    try {
      const xml = await SabrinaService.getNachrichtResponseXml(id);
      setXmlDialog(prev => prev.open ? { open: true, title: 'Response XML', content: xml || 'Kein XML-Inhalt vorhanden' } : prev);
    } catch {
      setXmlDialog(prev => prev.open ? { open: true, title: 'Response XML', content: 'Fehler beim Laden des XML-Inhalts' } : prev);
    } finally {
      setXmlLoading(false);
    }
  };

  // XML on-demand laden: Response-XML einer empfangenen Nachricht
  const handleShowEmpfangeneResponseXml = async (id: number) => {
    setXmlDialog({ open: true, title: 'Response XML (Empfangen)', content: '' });
    setXmlLoading(true);
    try {
      const xml = await SabrinaService.getEmpfangeneNachrichtResponseXml(id);
      setXmlDialog(prev => prev.open ? { open: true, title: 'Response XML (Empfangen)', content: xml || 'Kein XML-Inhalt vorhanden' } : prev);
    } catch {
      setXmlDialog(prev => prev.open ? { open: true, title: 'Response XML (Empfangen)', content: 'Fehler beim Laden des XML-Inhalts' } : prev);
    } finally {
      setXmlLoading(false);
    }
  };

  // Dokument herunterladen
  const handleDownloadDokument = async (dokumentId: number) => {
    try {
      await SabrinaService.downloadDokument(dokumentId);
    } catch (err: any) {
      console.error('Fehler beim Herunterladen des SABRINA-Dokuments:', err);
    }
  };

  // Status-Chip Renderer
  const getStatusChip = (status: SabrinaNachrichtStatus) => {
    const statusConfig: Record<SabrinaNachrichtStatus, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; icon: React.ReactElement }> = {
      [SabrinaNachrichtStatus.ERSTELLT]: { color: 'default', icon: <ScheduleIcon fontSize="small" /> },
      [SabrinaNachrichtStatus.GESENDET]: { color: 'info', icon: <MailIcon fontSize="small" /> },
      [SabrinaNachrichtStatus.EMPFANGEN]: { color: 'primary', icon: <DownloadIcon fontSize="small" /> },
      [SabrinaNachrichtStatus.VERARBEITET]: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
      [SabrinaNachrichtStatus.FEHLER]: { color: 'error', icon: <ErrorIcon fontSize="small" /> }
    };
    const config = statusConfig[status] || { color: 'default', icon: <InfoIcon fontSize="small" /> };
    return <Chip size="small" label={status} color={config.color} icon={config.icon} />;
  };

  // Nachrichtentyp Label
  const getNachrichtenTypLabel = (typ: string): string => {
    const labels: Record<string, string> = {
      '0006': 'Posteingang-Anfrage',
      '0007': 'Posteingang-Ergebnis',
      '0008': 'Nachrichtenabruf',
      '0001': 'Sammelnachricht',
      '0002': 'Quittung'
    };
    return labels[typ] || typ;
  };

  // Sortierte und paginierte empfangene Nachrichten
  const sortedEmpfangene = [...empfangeneNachrichten].sort((a, b) =>
    new Date(b.erstelltAm).getTime() - new Date(a.erstelltAm).getTime()
  );
  const paginatedEmpfangene = sortedEmpfangene.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Gefilterte, sortierte und paginierte Kommunikationsnachrichten (Dialog)
  const kommDefaultVon = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const kommDefaultBis = new Date().toISOString().split('T')[0];
  const filteredKomm = kommNachrichten.filter((n) => {
    if (!n.erstelltAm) return true;
    const date = new Date(n.erstelltAm);
    const from = kommVon ? new Date(kommVon + 'T00:00:00') : null;
    const to = kommBis ? new Date(kommBis + 'T23:59:59') : null;
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
  });
  const sortedKomm = [...filteredKomm].sort((a, b) =>
    new Date(b.erstelltAm).getTime() - new Date(a.erstelltAm).getTime()
  );
  const paginatedKomm = sortedKomm.slice(
    kommPage * kommRowsPerPage,
    kommPage * kommRowsPerPage + kommRowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          <MailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          SABRINA - Asynchroner Nachrichtenabruf
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={handleOpenKommDialog}
        >
          Kommunikationsnachrichten
        </Button>
      </Box>

      {/* Info-Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>KBA SABRINA-System</AlertTitle>
        Asynchroner Nachrichtenaustausch gemäß § 37 Abs. 3 FZV. 
        <strong> Polling ist maximal 1x pro Minute erlaubt.</strong>
      </Alert>

      {/* ===== Posteingang Card ===== */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📬 Posteingang prüfen
          </Typography>

          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button
              variant="contained"
              startIcon={posteingangLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={handleCheckPosteingang}
              disabled={posteingangLoading || cooldownSeconds > 0}
            >
              {cooldownSeconds > 0 ? `Warten (${cooldownSeconds}s)` : 'Posteingang prüfen'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadPosteingangListe}
              disabled={posteingangListeLoading}
            >
              Liste neu laden
            </Button>
            {lastPosteingangCheck && (
              <Typography variant="body2" color="text.secondary">
                Letzter KBA-Abruf: {lastPosteingangCheck.toLocaleString('de-DE')}
              </Typography>
            )}
          </Box>

          {cooldownSeconds > 0 && (
            <LinearProgress
              variant="determinate"
              value={(1 - cooldownSeconds / POLLING_COOLDOWN_SECONDS) * 100}
              sx={{ mb: 2 }}
            />
          )}

          {posteingangError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {posteingangError}
            </Alert>
          )}

          {posteingangResult && (
            <Box sx={{ mb: 2 }}>
              {posteingangResult.erfolg ? (
                posteingangResult.anzahlNachrichten > 0 ? (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    {posteingangResult.anzahlNachrichten} neue Nachricht(en) verfügbar
                  </Alert>
                ) : (
                  <Alert severity="info" icon={<InfoIcon />}>
                    {posteingangResult.hinweis || 'Keine neuen Nachrichten verfügbar'}
                  </Alert>
                )
              ) : (
                <Alert severity="warning" icon={<WarningIcon />}>
                  {posteingangResult.fehlerMeldung || 'Fehler beim Prüfen des Posteingangs'}
                  {posteingangResult.fehlerCode && ` (Code: ${posteingangResult.fehlerCode})`}
                </Alert>
              )}
            </Box>
          )}

          {/* Posteingang-Liste aus DB */}
          {posteingangListe.length > 0 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  📋 Verfügbare Nachrichten ({posteingangListe.length})
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedNachrichtenIds.length === posteingangListe.length && posteingangListe.length > 0}
                      indeterminate={selectedNachrichtenIds.length > 0 && selectedNachrichtenIds.length < posteingangListe.length}
                      onChange={handleSelectAll}
                    />
                  }
                  label="Alle auswählen"
                />
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Box display="flex" flexDirection="column" gap={1}>
                {posteingangListe.map((eintrag) => (
                  <Paper
                    key={eintrag.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: selectedNachrichtenIds.includes(eintrag.sabrinaNachrichtenId) ? 'action.selected' : 'transparent'
                    }}
                  >
                    <Checkbox
                      checked={selectedNachrichtenIds.includes(eintrag.sabrinaNachrichtenId)}
                      onChange={() => handleToggleNachricht(eintrag.sabrinaNachrichtenId)}
                    />
                    <Box flex={1}>
                      <Typography variant="body1" fontWeight="medium">
                        {eintrag.kommunikationsschrittName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {eintrag.sabrinaNachrichtenId} &bull; Code: {eintrag.kommunikationsschrittCode} &bull; Absender: {eintrag.absenderKps}
                        {eintrag.nachrichtenGroesse != null && ` • ${eintrag.nachrichtenGroesse} KB`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Erstellt: {new Date(eintrag.zeitpunktErstellung).toLocaleString('de-DE')}
                        {eintrag.bezugsnachrichtId && ` • Bezug: ${eintrag.bezugsnachrichtId}`}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
        {posteingangListe.length > 0 && (
          <CardActions sx={{ px: 2, pb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={abrufLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleAbrufNachrichten}
              disabled={abrufLoading || selectedNachrichtenIds.length === 0}
            >
              {abrufLoading ? 'Wird abgerufen...' : `Ausgewählte abrufen (${selectedNachrichtenIds.length})`}
            </Button>
          </CardActions>
        )}
      </Card>

      {/* Abruf-Ergebnis */}
      {abrufResult && (
        <Alert
          severity={abrufResult.erfolg ? 'success' : 'warning'}
          sx={{ mb: 3 }}
          onClose={() => setAbrufResult(null)}
        >
          <AlertTitle>Abruf-Ergebnis</AlertTitle>
          {abrufResult.anzahlAbgerufen} Nachricht(en) erfolgreich abgerufen.
          {abrufResult.fehler.length > 0 && (
            <Box mt={1}>
              <Typography variant="body2" fontWeight="bold">Fehler:</Typography>
              {abrufResult.fehler.map((fehler, index) => (
                <Typography key={index} variant="body2">
                  • {fehler.nachrichtenId}: {fehler.fehlerMeldung} (Code: {fehler.fehlerCode})
                </Typography>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {/* ===== Empfangene Nachrichten (Hauptbereich) ===== */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              📨 Empfangene SABRINA-Nachrichten
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadEmpfangeneNachrichten}
              disabled={empfangeneLoading}
            >
              Aktualisieren
            </Button>
          </Box>

          {empfangeneLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}></TableCell>
                      <TableCell>KBA-Antrags-ID</TableCell>
                      <TableCell>Kommunikationsschritt</TableCell>
                      <TableCell>FIN</TableCell>
                      <TableCell>GV-Art</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Gebühren</TableCell>
                      <TableCell>Dokumente</TableCell>
                      <TableCell>Antrag</TableCell>
                      <TableCell>Erstellt am</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedEmpfangene.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          Keine empfangenen Nachrichten vorhanden
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedEmpfangene.map((nachricht) => (
                        <React.Fragment key={nachricht.id}>
                          <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedRowId(expandedRowId === nachricht.id ? null : nachricht.id)}>
                            <TableCell>
                              <IconButton size="small">
                                {expandedRowId === nachricht.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {nachricht.kbaAntragsId || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={nachricht.kommunikationsschrittCode}>
                                <span>{nachricht.kommunikationsschrittName}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {nachricht.fin || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>{nachricht.geschaeftsvorfallArt || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={nachricht.antragStatus || '-'}
                                color={nachricht.antragStatus === 'bearbeitet' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {nachricht.gebuehrenGesamt != null
                                ? `${nachricht.gebuehrenGesamt.toFixed(2)} €`
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {nachricht.dokumente?.length > 0 ? (
                                <Chip size="small" icon={<PdfIcon />} label={nachricht.dokumente.length} color="primary" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {nachricht.zulassungsantrag?.id ? (
                                <MuiLink
                                  component="button"
                                  variant="body2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/zulassungsantraege/${nachricht.zulassungsantrag!.id}`);
                                  }}
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  #{nachricht.zulassungsantrag.id} <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
                                </MuiLink>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {new Date(nachricht.erstelltAm).toLocaleString('de-DE')}
                            </TableCell>
                          </TableRow>
                          {/* Expanded detail row */}
                          <TableRow>
                            <TableCell colSpan={10} sx={{ py: 0, borderBottom: expandedRowId === nachricht.id ? undefined : 'none' }}>
                              <Collapse in={expandedRowId === nachricht.id} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2 }}>
                                  <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mb={2}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Nachrichten-ID</Typography>
                                      <Typography variant="body2">{nachricht.sabrinaNachrichtenId}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Nachrichtentyp</Typography>
                                      <Typography variant="body2">{nachricht.nachrichtentyp}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Bearbeitungsart</Typography>
                                      <Typography variant="body2">{nachricht.bearbeitungsart || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Gebührentyp</Typography>
                                      <Typography variant="body2">{nachricht.gebuehrenTyp || '-'}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Wirksamkeit</Typography>
                                      <Typography variant="body2">
                                        {nachricht.datumWirksamkeit
                                          ? new Date(nachricht.datumWirksamkeit).toLocaleDateString('de-DE')
                                          : '-'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Absender KPS</Typography>
                                      <Typography variant="body2">{nachricht.absenderKps || '-'}</Typography>
                                    </Box>
                                  </Box>

                                  {/* Dokumente */}
                                  {nachricht.dokumente?.length > 0 && (
                                    <Box>
                                      <Typography variant="subtitle2" gutterBottom>Dokumente</Typography>
                                      <Box display="flex" flexWrap="wrap" gap={1}>
                                        {nachricht.dokumente.map((dok) => (
                                          <Button
                                            key={dok.id}
                                            variant="outlined"
                                            size="small"
                                            startIcon={<PdfIcon />}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownloadDokument(dok.id);
                                            }}
                                          >
                                            {dok.artDesDokuments}
                                            {dok.dokumentInformation && ` (${dok.dokumentInformation})`}
                                          </Button>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                  {/* Response XML on-demand */}
                                  <Box mt={1}>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<CodeIcon />}
                                      onClick={(e) => { e.stopPropagation(); handleShowEmpfangeneResponseXml(nachricht.id); }}
                                    >
                                      Response XML anzeigen
                                    </Button>
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={empfangeneNachrichten.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                labelRowsPerPage="Zeilen pro Seite:"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* ===== Kommunikationsnachrichten-Dialog ===== */}
      <Dialog
        open={kommDialogOpen}
        onClose={() => setKommDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              SABRINA-Kommunikationsnachrichten
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExportKommCsv}
                disabled={kommNachrichten.length === 0}
              >
                CSV Export
              </Button>
              <IconButton onClick={() => setKommDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Von"
                type="date"
                size="small"
                value={kommVon}
                onChange={(e) => { setKommVon(e.target.value); setKommPage(0); }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                label="Bis"
                type="date"
                size="small"
                value={kommBis}
                onChange={(e) => { setKommBis(e.target.value); setKommPage(0); }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={() => { setKommVon(kommDefaultVon); setKommBis(kommDefaultBis); setKommPage(0); }}
              >
                Zurücksetzen
              </Button>
              <Typography variant="body2" color="text.secondary">
                {filteredKomm.length} von {kommNachrichten.length} Einträgen
              </Typography>
            </Box>
          </Paper>
          {kommLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Nachrichten-ID</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Antrag-ID</TableCell>
                      <TableCell>Erstellt am</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedKomm.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Keine Kommunikationsnachrichten vorhanden
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedKomm.map((nachricht) => (
                        <TableRow key={nachricht.id} hover>
                          <TableCell>{nachricht.id}</TableCell>
                          <TableCell>{nachricht.nachrichtenId}</TableCell>
                          <TableCell>
                            <Tooltip title={nachricht.nachrichtenTyp as string}>
                              <span>{getNachrichtenTypLabel(nachricht.nachrichtenTyp as string)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{getStatusChip(nachricht.status)}</TableCell>
                          <TableCell>{nachricht.zulassungsantragId || '-'}</TableCell>
                          <TableCell>
                            {nachricht.erstelltAm
                              ? new Date(nachricht.erstelltAm).toLocaleString('de-DE')
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5}>
                              <Tooltip title="Request XML anzeigen">
                                <IconButton
                                  size="small"
                                  onClick={() => handleShowNachrichtRequestXml(nachricht.id)}
                                >
                                  <CodeIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Response XML anzeigen">
                                <IconButton
                                  size="small"
                                  onClick={() => handleShowNachrichtResponseXml(nachricht.id)}
                                >
                                  <CodeIcon fontSize="small" color="success" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Details anzeigen">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDetail(nachricht)}
                                  color="primary"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredKomm.length}
                rowsPerPage={kommRowsPerPage}
                page={kommPage}
                onPageChange={(_e, newPage) => setKommPage(newPage)}
                onRowsPerPageChange={(e) => { setKommRowsPerPage(parseInt(e.target.value, 10)); setKommPage(0); }}
                labelRowsPerPage="Zeilen pro Seite:"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKommDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Detail-Dialog (Kommunikationsnachricht) */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Nachricht-Details</Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedNachricht ? (
            <Box>
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">ID</Typography>
                  <Typography variant="body1">{selectedNachricht.id}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Nachrichten-ID</Typography>
                  <Typography variant="body1">{selectedNachricht.nachrichtenId}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Typ</Typography>
                  <Typography variant="body1">
                    {getNachrichtenTypLabel(selectedNachricht.nachrichtenTyp as string)} ({selectedNachricht.nachrichtenTyp})
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box mt={0.5}>{getStatusChip(selectedNachricht.status)}</Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Erstellt am</Typography>
                  <Typography variant="body1">
                    {new Date(selectedNachricht.erstelltAm).toLocaleString('de-DE')}
                  </Typography>
                </Box>
                {selectedNachricht.zulassungsantragId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Verknüpfter Antrag</Typography>
                    <Typography variant="body1">#{selectedNachricht.zulassungsantragId}</Typography>
                  </Box>
                )}
                {selectedNachricht.fehlerCode && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Fehler</Typography>
                    <Typography variant="body1" color="error">
                      {selectedNachricht.fehlerCode}: {selectedNachricht.fehlerMeldung}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box display="flex" gap={1} mt={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CodeIcon />}
                  onClick={() => handleShowNachrichtRequestXml(selectedNachricht.id)}
                >
                  Request XML anzeigen
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<CodeIcon />}
                  onClick={() => handleShowNachrichtResponseXml(selectedNachricht.id)}
                >
                  Response XML anzeigen
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography>Keine Daten verfügbar</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* XML-Dialog (Kommunikationsnachrichten) */}
      <XmlViewerDialog
        open={xmlDialog.open}
        title={xmlDialog.title}
        content={xmlDialog.content}
        loading={xmlLoading}
        onClose={() => setXmlDialog(prev => ({ ...prev, open: false }))}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SabrinaPage;
